import { useState, useEffect } from "react";
import { HourSlice } from "../../../../_helpers/components/Forms/HourSlice";
import { DefaultDropdown } from "../../../../_helpers/components/Forms/Dropdown";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { Button } from "../../../../_helpers/components/Button";

const WEEKDAYS = [
    { value: 1, label: "L" },
    { value: 2, label: "M" },
    { value: 3, label: "M" },
    { value: 4, label: "J" },
    { value: 5, label: "V" },
    { value: 6, label: "S" },
    { value: 7, label: "D" },
];

const WEEKDAY_FULL = ["", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const PERIOD_OPTIONS = [
    { value: "day", label: "Jour" },
    { value: "consecutive_days", label: "Jours consécutifs" },
    { value: "week", label: "Semaine" },
    { value: "month", label: "Mois" },
    { value: "export_period", label: "Période export" },
];

const RESULT_FORMAT_OPTIONS = [
    { value: "hours_cents", label: "Heures" },
    { value: "day_half", label: "Jour / ½" },
    { value: "full_day", label: "Jour (0/1)" },
];

const HOLIDAY_OPTIONS = [
    { value: "any", label: "Férié : indifférent" },
    { value: "must", label: "Férié uniquement" },
    { value: "must_not", label: "Hors férié" },
];

const periodLabel = (value) =>
    PERIOD_OPTIONS.find((o) => o.value === value)?.label ?? value;

const formatLabel = (value) =>
    RESULT_FORMAT_OPTIONS.find((o) => o.value === value)?.label ?? value;

export const ExportCalculationEditor = ({
    calculations,
    onChange,
    hourTypes,
}) => {
    const [expanded, setExpanded] = useState(() =>
        calculations.map((_, i) => i === 0)
    );
    const [showRanges, setShowRanges] = useState({});

    useEffect(() => {
        setExpanded((prev) => {
            if (prev.length === calculations.length) return prev;
            return calculations.map((_, i) => prev[i] ?? i === 0);
        });
    }, [calculations.length]);

    const toggleExpanded = (index) => {
        setExpanded((prev) => {
            const next = [...prev];
            next[index] = !next[index];
            return next;
        });
    };

    const updateCalculation = (index, field, value) => {
        const next = [...calculations];
        next[index] = { ...next[index], [field]: value };
        onChange(next);
    };

    const addCalculation = () => {
        onChange([
            ...calculations,
            {
                name: `CALC_${calculations.length + 1}`,
                payroll_rubric: "",
                hour_type_ids: hourTypes.length > 0 ? [hourTypes[0].id] : [],
                weekdays: [1, 2, 3, 4, 5, 6, 7],
                holiday_filter: "any",
                time_ranges: [],
                period_granularity: "export_period",
                min_duration_minutes: null,
                max_duration_minutes: null,
                result_format: "hours_cents",
            },
        ]);
        setExpanded((prev) => [...prev, true]);
    };

    const removeCalculation = (index) => {
        onChange(calculations.filter((_, i) => i !== index));
        setExpanded((prev) => prev.filter((_, i) => i !== index));
    };

    const toggleWeekday = (index, day) => {
        const current = calculations[index].weekdays || [];
        const next = current.includes(day)
            ? current.filter((d) => d !== day)
            : [...current, day].sort();
        updateCalculation(index, "weekdays", next);
    };

    const addTimeRange = (index) => {
        const ranges = [...(calculations[index].time_ranges || [])];
        ranges.push({ start: "22:00", end: "06:00" });
        updateCalculation(index, "time_ranges", ranges);
        setShowRanges((prev) => ({ ...prev, [index]: true }));
    };

    const updateTimeRange = (calcIndex, rangeIndex, field, value) => {
        const ranges = [...(calculations[calcIndex].time_ranges || [])];
        ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: value };
        updateCalculation(calcIndex, "time_ranges", ranges);
    };

    const removeTimeRange = (calcIndex, rangeIndex) => {
        const ranges = (calculations[calcIndex].time_ranges || []).filter(
            (_, i) => i !== rangeIndex
        );
        updateCalculation(calcIndex, "time_ranges", ranges);
    };

    const calcSummary = (calculation) => {
        const htCount = (calculation.hour_type_ids || []).length;
        const days = (calculation.weekdays || [])
            .map((d) => WEEKDAY_FULL[d])
            .join(", ");
        return [
            calculation.payroll_rubric && `Rub. ${calculation.payroll_rubric}`,
            htCount > 0 && `${htCount} type(s) d'heure`,
            periodLabel(calculation.period_granularity),
            formatLabel(calculation.result_format),
            days && days,
        ]
            .filter(Boolean)
            .join(" · ");
    };

    return (
        <div className="space-y-2">
            {calculations.map((calculation, index) => {
                const isOpen = expanded[index];
                const ranges = calculation.time_ranges || [];
                const rangesOpen = showRanges[index] || ranges.length > 0;

                return (
                    <div
                        key={index}
                        className="rounded-lg border border-gray-200 overflow-hidden bg-white"
                    >
                        <button
                            type="button"
                            onClick={() => toggleExpanded(index)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                        >
                            <svg
                                className={[
                                    "w-4 h-4 shrink-0 text-gray-400 transition-transform",
                                    isOpen ? "rotate-90" : "",
                                ].join(" ")}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <div className="flex-1 min-w-0">
                                <span className="font-medium text-gray-900 text-sm">
                                    {calculation.name || `Calcul ${index + 1}`}
                                </span>
                                {!isOpen && (
                                    <p className="text-xs text-gray-500 truncate mt-0.5">
                                        {calcSummary(calculation) || "Non configuré"}
                                    </p>
                                )}
                            </div>
                            {calculations.length > 1 && (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeCalculation(index);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.stopPropagation();
                                            removeCalculation(index);
                                        }
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800 px-2 py-1 shrink-0"
                                >
                                    Suppr.
                                </span>
                            )}
                        </button>

                        {isOpen && (
                            <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-3">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <InputForm
                                        name="Nom"
                                        value={calculation.name}
                                        onChange={(e) =>
                                            updateCalculation(
                                                index,
                                                "name",
                                                e.target.value
                                                    .toUpperCase()
                                                    .replace(/[^A-Z0-9_]/g, "_")
                                            )
                                        }
                                        placeholder="CALC_CONGES"
                                    />
                                    <InputForm
                                        name="Rubrique paie"
                                        value={calculation.payroll_rubric}
                                        onChange={(e) =>
                                            updateCalculation(index, "payroll_rubric", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-app-primary mb-1.5">
                                            Types d'heures
                                        </label>
                                        <select
                                            multiple
                                            size={Math.min(5, Math.max(3, hourTypes.length))}
                                            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 focus:bg-white focus:outline-none focus:border-gray-400"
                                            value={(calculation.hour_type_ids || []).map(String)}
                                            onChange={(e) => {
                                                const selected = Array.from(
                                                    e.target.selectedOptions
                                                ).map((opt) => parseInt(opt.value, 10));
                                                updateCalculation(index, "hour_type_ids", selected);
                                            }}
                                        >
                                            {hourTypes.map((ht) => (
                                                <option key={ht.id} value={ht.id}>
                                                    {ht.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <span className="block text-xs font-bold uppercase tracking-wide text-app-primary mb-1.5">
                                            Jours
                                        </span>
                                        <div className="flex gap-1">
                                            {WEEKDAYS.map((day, dayIdx) => (
                                                <button
                                                    key={day.value}
                                                    type="button"
                                                    title={WEEKDAY_FULL[day.value]}
                                                    onClick={() => toggleWeekday(index, day.value)}
                                                    className={[
                                                        "w-8 h-8 text-xs font-medium rounded-md border transition-colors",
                                                        (calculation.weekdays || []).includes(day.value)
                                                            ? "bg-app-primary text-white border-app-primary"
                                                            : "bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300",
                                                    ].join(" ")}
                                                >
                                                    {dayIdx === 2 ? "Me" : day.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    <DefaultDropdown
                                        name="Férié"
                                        value={calculation.holiday_filter}
                                        options={HOLIDAY_OPTIONS}
                                        onChange={(e) =>
                                            updateCalculation(index, "holiday_filter", e.target.value)
                                        }
                                    />
                                    <DefaultDropdown
                                        name="Période"
                                        value={calculation.period_granularity}
                                        options={PERIOD_OPTIONS}
                                        onChange={(e) =>
                                            updateCalculation(index, "period_granularity", e.target.value)
                                        }
                                    />
                                    <DefaultDropdown
                                        name="Résultat"
                                        value={calculation.result_format}
                                        options={RESULT_FORMAT_OPTIONS}
                                        onChange={(e) =>
                                            updateCalculation(index, "result_format", e.target.value)
                                        }
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <InputForm
                                            name="Min (en min.)"
                                            type="number"
                                            value={calculation.min_duration_minutes ?? ""}
                                            onChange={(e) =>
                                                updateCalculation(
                                                    index,
                                                    "min_duration_minutes",
                                                    e.target.value === ""
                                                        ? null
                                                        : parseInt(e.target.value, 10)
                                                )
                                            }
                                        />
                                        <InputForm
                                            name="Max (en min.)"
                                            type="number"
                                            value={calculation.max_duration_minutes ?? ""}
                                            onChange={(e) =>
                                                updateCalculation(
                                                    index,
                                                    "max_duration_minutes",
                                                    e.target.value === ""
                                                        ? null
                                                        : parseInt(e.target.value, 10)
                                                )
                                            }
                                        />
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowRanges((prev) => ({
                                                ...prev,
                                                [index]: !rangesOpen,
                                            }))
                                        }
                                        className="text-xs text-app-primary hover:underline"
                                    >
                                        {rangesOpen ? "▼" : "▶"} Plages horaires
                                        {ranges.length > 0 && ` (${ranges.length})`}
                                    </button>
                                    {rangesOpen && (
                                        <div className="mt-2 space-y-2">
                                            {ranges.map((range, rangeIndex) => (
                                                <HourSlice
                                                    key={rangeIndex}
                                                    index={rangeIndex}
                                                    nameStartTime="Début"
                                                    nameEndTime="Fin"
                                                    valueStartTime={range.start}
                                                    valueEndTime={range.end}
                                                    setStartTime={(value) =>
                                                        updateTimeRange(index, rangeIndex, "start", value)
                                                    }
                                                    setEndTime={(value) =>
                                                        updateTimeRange(index, rangeIndex, "end", value)
                                                    }
                                                    onRemove={() => removeTimeRange(index, rangeIndex)}
                                                    listHourTypes={[]}
                                                    valueHourType={null}
                                                    setHourType={() => {}}
                                                    classNameHourType="hidden"
                                                    desktopLayout="single"
                                                    classNameWrapper="mb-0"
                                                />
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addTimeRange(index)}
                                                className="text-xs text-gray-600 hover:text-gray-900"
                                            >
                                                + Ajouter une plage
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            <button
                type="button"
                onClick={addCalculation}
                className="w-full py-2 text-sm text-app-primary border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
                + Ajouter un calcul
            </button>
        </div>
    );
};
